import Link from 'next/link';

import clsxm from '@/lib/clsxm';

import type { CompetitorCard } from './types';
import { ArrowGlyph, SectionHeading, tone } from '../primitives';

const DOCS = 'https://easy-peasy.ai/docs/agents';

/** Links to the other comparisons and to the product pages and docs. */
export default function MoreComparisons({
  others,
}: {
  others: CompetitorCard[];
}) {
  return (
    <section id='more' className='py-20 sm:py-24'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between'>
          <SectionHeading
            size='md'
            title='More comparisons.'
            tail='Same rules: official sources, checked dates.'
          />
          <Link
            href='/alternatives'
            className='inline-flex flex-none items-center gap-1.5 text-[15px] font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
          >
            All comparisons
            <ArrowGlyph />
          </Link>
        </div>

        <ul className='mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
          {others.map((other) => (
            <li key={other.slug}>
              <Link
                href={`/alternatives/${other.slug}`}
                className={clsxm(
                  'group flex h-full flex-col rounded-[22px] p-6 transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_24px_50px_-30px_rgba(30,20,90,0.45)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
                  tone.surface,
                  tone.line,
                )}
              >
                <span className='flex items-center justify-between gap-3'>
                  <span className={clsxm('text-lg font-semibold', tone.ink)}>
                    <span className='whitespace-nowrap'>Easy-Peasy.AI</span> vs{' '}
                    {other.name}
                  </span>
                  {other.badge && (
                    <span className='rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-400/10 dark:text-amber-300'>
                      {other.badge}
                    </span>
                  )}
                </span>
                <span
                  className={clsxm(
                    'mt-2 flex-1 text-[15px] leading-6',
                    tone.body,
                  )}
                >
                  {other.summary}
                </span>
                <span className='mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 dark:text-primary-300'>
                  {other.name} alternative
                  <ArrowGlyph className='h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5' />
                </span>
              </Link>
            </li>
          ))}
        </ul>

        <div
          className={clsxm(
            'mt-10 flex flex-wrap gap-x-8 gap-y-3 border-t border-[#0f0f28]/[0.08] pt-8 text-[15px] dark:border-white/[0.08]',
            tone.body,
          )}
        >
          <Link
            href='/chatbots'
            className='font-medium hover:text-primary-700 dark:hover:text-primary-300'
          >
            AI agents overview
          </Link>
          <Link
            href='/chatbots/customer-service'
            className='font-medium hover:text-primary-700 dark:hover:text-primary-300'
          >
            Customer service agents
          </Link>
          <Link
            href='/chatbots/sales'
            className='font-medium hover:text-primary-700 dark:hover:text-primary-300'
          >
            Sales agents
          </Link>
          <a
            href={`${DOCS}/overview`}
            className='font-medium hover:text-primary-700 dark:hover:text-primary-300'
          >
            Agent docs
          </a>
          <a
            href={`${DOCS}/channels`}
            className='font-medium hover:text-primary-700 dark:hover:text-primary-300'
          >
            Channels
          </a>
          <a
            href={`${DOCS}/limits-and-usage`}
            className='font-medium hover:text-primary-700 dark:hover:text-primary-300'
          >
            Limits and usage
          </a>
        </div>
      </div>
    </section>
  );
}
